import { z } from 'zod';
import { baseEntityFields, idSchema, instanceIdSchema, isoDateTimeSchema, tenantIdSchema } from './common.schema';
/**
 * Browser remote access: SSH and RDP sessions through the collector (#158; design of record:
 * docs/architecture/remote-access.md). This file is the ONE definition of the data the broker (api), the relay and the
 * collector exchange and keep: the session record, the three kinds of token, the control-socket frames, the audit events,
 * the tenant policy, and the forbidden-address rule. The Go relay and the Go collector implement the wire formats from
 * the fixtures in shared/fixtures (remote-access-*.v1.json), as the collector updater does for the release manifest.
 *
 * Nothing here is a credential. A session record, a token or an audit event NEVER carries a password, a private key or a
 * terminal's contents: the schemas are strict and have nowhere to put one.
 */
export const REMOTE_PROTOCOLS = ['ssh', 'rdp'];
/**
 * v1 authorisation (PO, #158): opening a session needs this rung or above, with the membership's client scope covering the
 * resource. There is no separate permission; the target machine's own login is the real control. The permission-based RBAC
 * model is deferred to #164, where `resources.remote-session` is one of its permissions (docs §5.1).
 */
export const REMOTE_MIN_TENANT_ROLE = 'agent';
/**
 * Limits, in one place so the broker, the relay and the collector cannot disagree. A tenant policy may tighten the idle
 * time within these bounds; nothing may loosen the hard cap or the token life.
 */
export const REMOTE_LIMITS = {
    /** A session token is good for this long after it is issued, and for ONE use. */
    sessionTokenTtlSeconds: 60,
    /** A collector's control-socket token. The collector asks for a fresh one for each (re)connection. */
    controlTokenTtlSeconds: 300,
    /** Allowed clock difference between the broker, the relay and the collector when checking `exp`/`iat`. */
    clockSkewSeconds: 5,
    /** No session outlives this, whatever its activity. Matches the Cloud Run request cap of the relay. */
    hardCapMinutes: 60,
    defaultIdleMinutes: 15,
    /** A tenant may lower the idle time down to this (tests use it); never to zero. */
    minIdleMinutes: 1,
    maxIdleMinutes: 60,
    /** The collector reconnects its control socket this often, before the request cap would cut it (docs §9). */
    controlRotateMinutes: 55,
    /** Bytes each way, per session. Bounds the egress one session can cost (docs §3). */
    maxBytesPerDirection: 512 * 1024 * 1024,
    /** Fresh sign-in required for a session (Firebase `auth_time`). */
    stepUpMaxAgeMinutes: 10,
    maxConcurrentPerUser: 3,
    maxConcurrentPerCollector: 5,
    maxConcurrentPerTenant: 20,
    /** Destination ports a collector may open for each protocol unless the host configures others (and at most this many). */
    defaultPorts: { ssh: [22], rdp: [3389] },
    maxPortsPerProtocol: 5,
    /** The relay checks with the broker this often that a live session is still allowed to exist (the backstop for a lost terminate). */
    relayRevalidateSeconds: 30,
    /** A token longer than this is refused before it is decoded; the relay also caps a websocket message at this size until the token is verified. */
    maxTokenChars: 2048,
    /** The browser's first websocket message is the auth frame; the relay closes the socket if none valid arrives in this time (docs §6.4). */
    authTimeoutSeconds: 5,
    /** Largest message the relay accepts from a browser socket before the token has verified. */
    maxPreAuthMessageBytes: 4096,
    /** Largest control-socket frame (either direction) a peer will read or parse; a transport-level cap, enforced before parsing (docs §6.2). */
    maxControlFrameBytes: 64 * 1024,
    /** Collectors that may serve one resource: the primary plus failovers (docs §9.1). */
    maxCollectorsPerResource: 3,
    /** Trusted signing keys a verifier will hold (one, or two during a rotation); more is a misconfiguration and fails closed. */
    maxTrustedKeys: 2,
};
/** Sessions the shard model supports: shard ids are 0..shards-1. */
export const MAX_RELAY_SHARDS = 64;
const shardSchema = z.number().int().min(0).max(MAX_RELAY_SHARDS - 1);
/* ------------------------------ tenant policy ------------------------------ */
const portSchema = z.number().int().min(1).max(65535);
const portListSchema = z
    .array(portSchema)
    .min(1)
    .max(REMOTE_LIMITS.maxPortsPerProtocol)
    .refine((p) => new Set(p).size === p.length, { message: 'ports must be unique' });
/**
 * What an account admin sets for the tenant. Everything is OFF or at its strictest by default. The platform operator's
 * per-tenant FEATURE flag is separate (`tenant.features.remoteAccess`, part 4): this policy has no effect without it.
 */
export const remoteAccessPolicySchema = z
    .object({
    /** The tenant's own switch. Off = no sessions. A kill switch, not a plan feature (docs §5). */
    enabled: z.boolean(),
    protocols: z.array(z.enum(REMOTE_PROTOCOLS)).min(1).max(REMOTE_PROTOCOLS.length),
    idleMinutes: z.number().int().min(REMOTE_LIMITS.minIdleMinutes).max(REMOTE_LIMITS.maxIdleMinutes),
    /** Require an SSO sign-in (the identity provider's 2FA applies) in addition to the fresh-sign-in step-up. */
    requireSso: z.boolean(),
    /** RDP clipboard and drive redirection. Off by default; each is a data-exfiltration path. */
    allowClipboard: z.boolean(),
    allowDriveRedirection: z.boolean(),
    /** Optional: only resources of these clients may be connected to (intersected with the member's own client scope). */
    allowedClientIds: z.array(idSchema).min(1).max(500).nullable(),
})
    .strict()
    .superRefine((val, ctx) => {
    if (new Set(val.protocols).size !== val.protocols.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['protocols'], message: 'protocols must be unique' });
    }
    if (val.allowedClientIds !== null && new Set(val.allowedClientIds).size !== val.allowedClientIds.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['allowedClientIds'], message: 'allowedClientIds must be unique' });
    }
});
/** A tenant that has never set a policy: remote access is OFF, and the strictest settings apply if it is turned on. */
export const DEFAULT_REMOTE_ACCESS_POLICY = {
    enabled: false,
    protocols: ['ssh', 'rdp'],
    idleMinutes: REMOTE_LIMITS.defaultIdleMinutes,
    requireSso: false,
    allowClipboard: false,
    allowDriveRedirection: false,
    allowedClientIds: null,
};
/* ------------------------- collector participation ------------------------- */
/**
 * What a collector host reports about ITSELF: its local opt-in (docs §7.4). Off by default and only ever turned on on the
 * host. The cloud cannot turn it on; it can only read it, and a session is refused unless the collector says it is on.
 */
export const collectorRemoteAccessReportSchema = z
    .object({
    enabled: z.boolean(),
    ports: z
        .object({ ssh: portListSchema, rdp: portListSchema })
        .strict(),
})
    .strict();
/* ------------------------- collector selection and redundancy ------------------------- */
/** An ordered list of collectors that may serve one resource: the primary first, then failovers (no duplicates). */
const collectorIdsSchema = z
    .array(idSchema)
    .min(1)
    .max(REMOTE_LIMITS.maxCollectorsPerResource)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'collectors must be unique' });
/**
 * What part 4 attaches to a resource: the collectors that may serve its remote sessions. The resource's own `collectorId`
 * is the primary and is listed first; the others are failovers. A resource with one entry has no redundancy.
 */
export const remoteCollectorRoutingSchema = z.object({ collectorIds: collectorIdsSchema }).strict();
/** Why a collector was passed over, or `opened`. `offline` is observed by the broker/relay; the rest are the collector's own refusals. */
export const REMOTE_ATTEMPT_OUTCOMES = [
    'opened',
    'offline',
    'not-opted-in',
    'port-not-allowed',
    'address-forbidden',
    'target-not-assigned',
    'limit-reached',
    'connect-failed',
    'connect-timeout',
    'shutting-down',
];
export const remoteAttemptSchema = z.object({ collectorId: idSchema, outcome: z.enum(REMOTE_ATTEMPT_OUTCOMES), at: isoDateTimeSchema }).strict();
function candidatesInclude(ids, id) {
    return id === null || ids.includes(id);
}
/* -------------------------------- session record ------------------------------- */
export const REMOTE_SESSION_STATUSES = ['pending', 'connecting', 'active', 'ended'];
/** Why a session ended (`ended` only). */
export const REMOTE_END_REASONS = [
    'user-closed',
    'idle-timeout',
    'hard-cap',
    'terminated-by-admin',
    'kill-switch',
    'policy-changed',
    'collector-lost',
    'target-closed',
    'relay-restart',
    'auth-failed',
    'host-key-mismatch',
    'byte-limit',
    'connect-failed',
    'never-connected',
    'error',
];
/** How the session's bytes travel. Only `relay` in v1; `direct` is reserved for #165 (docs §17). */
export const REMOTE_TRANSPORTS = ['relay'];
/** The destination, as the BROKER resolved it from the resource record. A caller never supplies it. */
const targetShape = z
    .object({
    /** An IP address literal or a hostname, exactly as stored on the resource. */
    host: z.string().min(1).max(253),
    port: portSchema,
})
    .strict();
/**
 * Any destination that travels in a token, a session record or a control frame is checked against the forbidden-address
 * rule right here, so no layer can accept one by validating the shape alone.
 */
export const remoteTargetSchema = targetShape.superRefine((val, ctx) => {
    const reason = remoteForbiddenAddress(val.host);
    if (reason !== null)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['host'], message: `destination refused: ${reason}` });
});
/** The audit trail records a destination even when it was REFUSED (`target-forbidden`), so events use the bare shape. */
export const remoteAuditTargetSchema = targetShape;
export const remoteSessionSchema = z
    .object({
    userId: idSchema,
    resourceId: idSchema,
    /** Names as they were when the session started, so history reads correctly after a rename or delete. */
    resourceName: z.string().min(1).max(253),
    /** Healthy collectors the broker found for the resource, best first (docs §9.1). The relay tries them in this order. */
    candidateCollectorIds: collectorIdsSchema,
    /** The collector that actually served the session; null until one accepted the open. */
    collectorId: idSchema.nullable(),
    /** One entry per candidate the relay passed over or tried, in candidate order (a prefix of `candidateCollectorIds`); the last is the one that served, if any did. */
    attempts: z.array(remoteAttemptSchema).max(REMOTE_LIMITS.maxCollectorsPerResource),
    transport: z.enum(REMOTE_TRANSPORTS),
    protocol: z.enum(REMOTE_PROTOCOLS),
    target: remoteTargetSchema,
    shard: shardSchema,
    status: z.enum(REMOTE_SESSION_STATUSES),
    /** The session token's `jti`: single use, bound to this record. */
    tokenId: z.string().uuid(),
    createdAt: isoDateTimeSchema,
    /** After this the token is dead and an unconnected session becomes `never-connected`. */
    connectBy: isoDateTimeSchema,
    startedAt: isoDateTimeSchema.nullable(),
    /** startedAt + the hard cap. */
    hardDeadlineAt: isoDateTimeSchema.nullable(),
    idleMinutes: z.number().int().min(REMOTE_LIMITS.minIdleMinutes).max(REMOTE_LIMITS.maxIdleMinutes),
    lastActivityAt: isoDateTimeSchema.nullable(),
    endedAt: isoDateTimeSchema.nullable(),
    endReason: z.enum(REMOTE_END_REASONS).nullable(),
    /** Who ended it, when someone did (`terminated-by-admin`, `kill-switch`). */
    endedBy: idSchema.nullable(),
    bytesToTarget: z.number().int().nonnegative(),
    bytesFromTarget: z.number().int().nonnegative(),
    /** The fresh sign-in this session rests on. */
    stepUp: z.object({ authTime: isoDateTimeSchema, signInProvider: z.string().max(64).nullable() }).strict(),
    clientIp: z.string().max(64).nullable(),
})
    .extend(baseEntityFields)
    .strict()
    .superRefine((val, ctx) => {
    const bad = (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    const ended = val.status === 'ended';
    if (ended !== (val.endedAt !== null))
        bad('endedAt', 'endedAt is set exactly when the session has ended');
    if (ended !== (val.endReason !== null))
        bad('endReason', 'endReason is set exactly when the session has ended');
    if (!ended && val.endedBy !== null)
        bad('endedBy', 'endedBy is only for an ended session');
    if (val.status === 'pending' && val.startedAt !== null)
        bad('startedAt', 'a pending session has not started');
    if (val.status === 'active' && (val.startedAt === null || val.hardDeadlineAt === null)) {
        bad('startedAt', 'an active session has startedAt and hardDeadlineAt');
    }
    if ((val.startedAt === null) !== (val.hardDeadlineAt === null))
        bad('hardDeadlineAt', 'startedAt and hardDeadlineAt are set together');
    if (val.startedAt !== null && val.hardDeadlineAt !== null) {
        const cap = Date.parse(val.startedAt) + REMOTE_LIMITS.hardCapMinutes * 60_000;
        if (Date.parse(val.hardDeadlineAt) <= Date.parse(val.startedAt))
            bad('hardDeadlineAt', 'the deadline is after the start');
        if (Date.parse(val.hardDeadlineAt) > cap)
            bad('hardDeadlineAt', `a session cannot be allowed longer than ${REMOTE_LIMITS.hardCapMinutes} minutes`);
    }
    if (val.endReason === 'never-connected' && val.startedAt !== null)
        bad('endReason', 'a session that started was connected');
    if (!candidatesInclude(val.candidateCollectorIds, val.collectorId))
        bad('collectorId', 'the serving collector is one of the candidates');
    if (val.status === 'active' && val.collectorId === null)
        bad('collectorId', 'an active session has a serving collector');
    if (val.status === 'pending' && (val.collectorId !== null || val.attempts.length > 0))
        bad('attempts', 'a pending session has tried no collector');
    if (val.startedAt !== null && val.collectorId === null)
        bad('collectorId', 'a session that started was served by a collector');
    const last = val.attempts[val.attempts.length - 1];
    // `opened` and a serving collector go together: the serving collector is the last attempt, and only it opened.
    if ((val.collectorId !== null) !== (last !== undefined && last.outcome === 'opened'))
        bad('attempts', 'a serving collector is set exactly when the last attempt opened');
    if (val.collectorId !== null && last !== undefined && last.collectorId !== val.collectorId)
        bad('attempts', 'the serving collector is the last attempt');
    if (val.attempts.some((a, n) => a.outcome === 'opened' && n !== val.attempts.length - 1))
        bad('attempts', 'only the last attempt can have opened');
    // The relay walks the candidates IN ORDER (docs §9.1): the attempts are a PREFIX of the candidate list, one entry per
    // candidate passed over or tried, so attempt n is always candidate n (which also makes them distinct candidates).
    val.attempts.forEach((a, n) => {
        if (a.collectorId !== val.candidateCollectorIds[n])
            bad('attempts', `attempt ${n + 1} must be candidate ${n + 1}: the relay tries the candidates in order`);
    });
});
/* ----------------------------------- tokens ----------------------------------- */
/**
 * Wire format of every token: `base64url(payload) "." base64url(signature)`, where payload is the exact UTF-8 JSON of the
 * claims and the signature is Ed25519 over those exact bytes. A verifier checks the signature over the bytes it received
 * BEFORE parsing them, then parses strictly (docs §6.1). `kid` names the key; two may be valid during a rotation.
 */
export const REMOTE_TOKEN_TYPES = {
    session: 'detect-remote-session',
    control: 'detect-remote-control',
};
const tokenCommon = {
    v: z.literal(1),
    kid: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/),
    /** Unique per token. The relay remembers a session token's jti until it expires and refuses a second use. */
    jti: z.string().uuid(),
    iat: z.number().int().positive(),
    exp: z.number().int().positive(),
};
/** Browser -> relay: "this user may open THIS session to THIS target through THIS collector, once, in the next minute." */
export const remoteSessionTokenClaimsSchema = z
    .object({
    typ: z.literal(REMOTE_TOKEN_TYPES.session),
    ...tokenCommon,
    tenantId: tenantIdSchema,
    userId: idSchema,
    sessionId: idSchema,
    resourceId: idSchema,
    /** The collectors the broker approved for this session, best first; the relay tries them in order and no others. */
    collectorIds: collectorIdsSchema,
    protocol: z.enum(REMOTE_PROTOCOLS),
    target: remoteTargetSchema,
    /** The relay shard this session must run on; any other shard refuses the token. */
    shard: shardSchema,
    limits: z
        .object({
        idleSeconds: z.number().int().min(REMOTE_LIMITS.minIdleMinutes * 60).max(REMOTE_LIMITS.maxIdleMinutes * 60),
        hardSeconds: z.number().int().positive().max(REMOTE_LIMITS.hardCapMinutes * 60),
        maxBytesPerDirection: z.number().int().positive().max(REMOTE_LIMITS.maxBytesPerDirection),
    })
        .strict()
        .superRefine(idleWithinHard),
    clipboard: z.boolean(),
    driveRedirection: z.boolean(),
})
    .strict()
    .superRefine(tokenWindow(REMOTE_LIMITS.sessionTokenTtlSeconds));
/** Collector -> relay: "this is collector X of tenant T, for shard S." Minted by the api for a collector that proved its own token. */
export const remoteControlTokenClaimsSchema = z
    .object({
    typ: z.literal(REMOTE_TOKEN_TYPES.control),
    ...tokenCommon,
    tenantId: tenantIdSchema,
    collectorId: idSchema,
    shard: shardSchema,
})
    .strict()
    .superRefine(tokenWindow(REMOTE_LIMITS.controlTokenTtlSeconds));
function tokenWindow(maxTtlSeconds) {
    return (val, ctx) => {
        if (val.exp <= val.iat) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['exp'], message: 'exp must be after iat' });
        }
        else if (val.exp - val.iat > maxTtlSeconds) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['exp'], message: `a token lives at most ${maxTtlSeconds} seconds` });
        }
    };
}
/* ------------------------- control-socket frames (relay <-> collector) ------------------------- */
/** Why a collector refused to open a session. The relay passes it to the broker's audit; the browser sees a generic message. */
export const REMOTE_REFUSAL_REASONS = [
    'not-opted-in',
    'port-not-allowed',
    'address-forbidden',
    'target-not-assigned',
    'limit-reached',
    'connect-failed',
    'connect-timeout',
    'shutting-down',
];
const capabilitiesSchema = z.array(z.string().regex(/^[a-z0-9-]{1,32}$/)).max(8);
const sessionLimitsSchema = z
    .object({
    idleSeconds: z.number().int().positive().max(REMOTE_LIMITS.maxIdleMinutes * 60),
    hardSeconds: z.number().int().positive().max(REMOTE_LIMITS.hardCapMinutes * 60),
    maxBytesPerDirection: z.number().int().positive().max(REMOTE_LIMITS.maxBytesPerDirection),
})
    .strict()
    .superRefine(idleWithinHard);
function idleWithinHard(val, ctx) {
    if (val.idleSeconds > val.hardSeconds) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['idleSeconds'], message: 'the idle time cannot exceed the hard cap' });
    }
}
/** Frames the collector sends on its control socket. */
export const collectorToRelayFrameSchema = z.discriminatedUnion('type', [
    z
        .object({
        type: z.literal('hello'),
        v: z.literal(1),
        collectorId: idSchema,
        version: z.string().min(1).max(64),
        remoteAccess: collectorRemoteAccessReportSchema,
        /** Optional features this collector supports. Empty in v1; reserved so a later transport can be negotiated (docs §17). */
        capabilities: capabilitiesSchema,
        /** Sessions this collector believes are open (after a reconnect), so the relay can reconcile. */
        openSessions: z.array(idSchema).max(REMOTE_LIMITS.maxConcurrentPerCollector),
    })
        .strict(),
    z.object({ type: z.literal('opened'), sessionId: idSchema }).strict(),
    z.object({ type: z.literal('refused'), sessionId: idSchema, reason: z.enum(REMOTE_REFUSAL_REASONS) }).strict(),
    z
        .object({
        type: z.literal('closed'),
        sessionId: idSchema,
        bytesToTarget: z.number().int().nonnegative(),
        bytesFromTarget: z.number().int().nonnegative(),
    })
        .strict(),
    z.object({ type: z.literal('pong') }).strict(),
]);
/** Frames the relay sends. `open` carries a one-time nonce the collector presents on the per-session DATA socket. */
export const relayToCollectorFrameSchema = z.discriminatedUnion('type', [
    z
        .object({
        type: z.literal('welcome'),
        v: z.literal(1),
        shard: shardSchema,
        /** Empty in v1 (docs §17). */
        capabilities: capabilitiesSchema,
        pingSeconds: z.number().int().positive().max(120),
        rotateAfterSeconds: z.number().int().positive().max(REMOTE_LIMITS.controlRotateMinutes * 60),
    })
        .strict(),
    z
        .object({
        type: z.literal('open'),
        sessionId: idSchema,
        target: remoteTargetSchema,
        protocol: z.enum(REMOTE_PROTOCOLS),
        /** 32 random bytes, base64url. Single use; valid for the connect window only. */
        nonce: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
        limits: sessionLimitsSchema,
    })
        .strict(),
    z.object({ type: z.literal('terminate'), sessionId: idSchema, reason: z.enum(['terminated-by-admin', 'kill-switch', 'policy-changed', 'hard-cap', 'idle-timeout', 'relay-restart']) }).strict(),
    z.object({ type: z.literal('ping') }).strict(),
    /** The relay is draining: reconnect to the same shard (a new instance), finishing nothing new on this socket. */
    z.object({ type: z.literal('drain') }).strict(),
]);
/**
 * The FIRST message on a browser websocket (docs §6.4). The token never travels in a URL or a header (proxies log both and
 * a subprotocol header is echoed back). Until it verifies, the relay sends nothing and accepts nothing larger than
 * `maxPreAuthMessageBytes`; it closes the socket if no valid frame arrives within `authTimeoutSeconds`.
 */
export const browserAuthFrameSchema = z
    .object({ type: z.literal('auth'), v: z.literal(1), token: z.string().min(1).max(REMOTE_LIMITS.maxTokenChars) })
    .strict();
/** The first message on a per-session DATA socket, collector -> relay; after it the socket is raw bytes. */
export const dataSocketOpenSchema = z.object({ v: z.literal(1), sessionId: idSchema, nonce: z.string().regex(/^[A-Za-z0-9_-]{43}$/) }).strict();
/* ----------------------------------- audit ----------------------------------- */
/** `SHA256:` + unpadded base64 of a host key's (or certificate's) SHA-256 digest: one format for the pinned record and the audit events. */
export const remoteHostKeyFingerprintSchema = z.string().regex(/^SHA256:[A-Za-z0-9+/]{43}$/);
/** Why a request for a session was refused. */
export const REMOTE_DENIAL_REASONS = [
    'feature-disabled',
    'tenant-disabled',
    'kill-switch-global',
    'kill-switch-tenant',
    'kill-switch-collector',
    'insufficient-role',
    'out-of-scope',
    'step-up-required',
    'sso-required',
    'protocol-not-allowed',
    'resource-has-no-address',
    'target-not-assigned',
    'target-forbidden',
    'collector-offline',
    'collector-not-opted-in',
    'collector-unsupported',
    'concurrency-limit',
    'rate-limited',
];
export const REMOTE_EVENT_KINDS = [
    'session.requested',
    'session.denied',
    'session.token_issued',
    'session.collector_skipped',
    'session.started',
    'session.terminate_requested',
    'session.ended',
    'host_key.trusted',
    'host_key.mismatch',
    'host_key.retrusted',
    'policy.changed',
    'kill_switch.set',
    'kill_switch.cleared',
    'collector.opt_in_changed',
];
export const remoteEventActorSchema = z
    .object({
    kind: z.enum(['user', 'collector', 'relay', 'system']),
    /** The user id, collector id, shard name or `system`. */
    id: z.string().min(1).max(128),
    delegatedFromTenantId: tenantIdSchema.optional(),
})
    .strict();
/**
 * One immutable audit record (`remoteAccessEvents`, append-only: created, never updated or deleted; streamed to BigQuery).
 * Every field is optional beyond the identity of the event, and only the ones that apply to its kind are set (checked below).
 */
export const remoteAccessEventSchema = z
    .object({
    tenantId: tenantIdSchema,
    at: isoDateTimeSchema,
    kind: z.enum(REMOTE_EVENT_KINDS),
    actor: remoteEventActorSchema,
    sessionId: idSchema.nullable(),
    resourceId: idSchema.nullable(),
    collectorId: idSchema.nullable(),
    protocol: z.enum(REMOTE_PROTOCOLS).nullable(),
    target: remoteAuditTargetSchema.nullable(),
    /** `session.denied` only. */
    denial: z.enum(REMOTE_DENIAL_REASONS).nullable(),
    /** `session.ended` only. */
    endReason: z.enum(REMOTE_END_REASONS).nullable(),
    bytesToTarget: z.number().int().nonnegative().nullable(),
    bytesFromTarget: z.number().int().nonnegative().nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    /** SHA-256 fingerprint (`SHA256:...`) for the host-key events: the key trusted or observed. */
    fingerprint: remoteHostKeyFingerprintSchema.nullable(),
    /** `host_key.mismatch` and `host_key.retrusted`: the fingerprint that was pinned before (docs §8.2). */
    previousFingerprint: remoteHostKeyFingerprintSchema.nullable(),
    /** `session.collector_skipped`: why this collector was passed over (docs §9.1). */
    attempt: z.enum(REMOTE_ATTEMPT_OUTCOMES).nullable(),
    /** `kill_switch.*`: what it covers. */
    scope: z.enum(['global', 'tenant', 'collector']).nullable(),
    clientIp: z.string().max(64).nullable(),
})
    .strict()
    .superRefine((val, ctx) => {
    const bad = (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    if ((val.kind === 'session.denied') !== (val.denial !== null))
        bad('denial', 'a denial reason is set exactly on session.denied');
    if ((val.kind === 'session.ended') !== (val.endReason !== null))
        bad('endReason', 'an end reason is set exactly on session.ended');
    const needsSession = ['session.token_issued', 'session.started', 'session.terminate_requested', 'session.ended'];
    if (needsSession.includes(val.kind) && val.sessionId === null)
        bad('sessionId', `${val.kind} belongs to a session`);
    if (val.kind.startsWith('kill_switch.') !== (val.scope !== null))
        bad('scope', 'a scope is set exactly on kill_switch events');
    if ((val.kind === 'host_key.mismatch' || val.kind === 'host_key.retrusted') !== (val.previousFingerprint !== null)) {
        bad('previousFingerprint', 'mismatch and re-trust events carry the previously pinned fingerprint, and only they do');
    }
    if (val.kind.startsWith('host_key.') && val.fingerprint === null)
        bad('fingerprint', 'host-key events carry the fingerprint');
    if ((val.kind === 'session.requested' || val.kind === 'session.denied') && (val.resourceId === null || val.protocol === null)) {
        bad('resourceId', `${val.kind} names the resource and protocol asked for`);
    }
    if (val.kind.startsWith('host_key.') && (val.resourceId === null || val.protocol === null || val.target === null)) {
        bad('resourceId', 'host-key events name the resource, protocol and target');
    }
    if ((val.kind === 'session.collector_skipped') !== (val.attempt !== null))
        bad('attempt', 'a skip reason is set exactly on session.collector_skipped');
    if (val.attempt === 'opened')
        bad('attempt', 'a skipped collector did not open');
    if (val.kind === 'session.collector_skipped' && (val.sessionId === null || val.collectorId === null))
        bad('collectorId', 'a skipped collector belongs to a session');
    if (val.kind === 'session.started' && (val.resourceId === null || val.collectorId === null || val.protocol === null || val.target === null)) {
        bad('resourceId', 'session.started records where the session went');
    }
    if (val.kind === 'session.ended' && (val.bytesToTarget === null || val.bytesFromTarget === null || val.durationMs === null)) {
        bad('bytesToTarget', 'session.ended carries the final byte counts and the duration');
    }
    if (val.kind !== 'session.ended' && (val.bytesToTarget !== null || val.bytesFromTarget !== null || val.durationMs !== null)) {
        bad('bytesToTarget', 'byte counts and duration are recorded on session.ended only');
    }
});
/* ----------------------- durable single-use claims and the shard fence ----------------------- */
/** `remoteTokenClaims/{jti}`: created in a transaction only if absent; a second create is a replay. Expires with the token. */
export const remoteTokenClaimSchema = z
    .object({ jti: z.string().uuid(), typ: z.enum([REMOTE_TOKEN_TYPES.session, REMOTE_TOKEN_TYPES.control]), claimedAt: isoDateTimeSchema, expiresAt: isoDateTimeSchema })
    .strict();
/** `remoteShardLeases/{shard}`: the instance fence (docs §9). Platform-level; the relay renews it, nothing else writes it. */
export const remoteShardLeaseSchema = z
    .object({ shard: shardSchema, holder: z.string().min(1).max(128), acquiredAt: isoDateTimeSchema, renewedAt: isoDateTimeSchema, expiresAt: isoDateTimeSchema })
    .strict();
/* ------------------------------- host-key trust ------------------------------- */
/**
 * The host key (SSH) or certificate (RDP) trusted for a resource: first use is trusted and recorded; a different one later
 * BLOCKS the session until someone who can open the session re-trusts it after an explicit confirmation (audited, docs §8.2). One record per (tenant, resource, protocol, port).
 */
export const remoteHostKeySchema = z
    .object({
    resourceId: idSchema,
    protocol: z.enum(REMOTE_PROTOCOLS),
    port: portSchema,
    /** `SHA256:` + unpadded base64 of the key's SHA-256 digest. */
    fingerprint: remoteHostKeyFingerprintSchema,
    /** The key type (`ssh-ed25519`, `ecdsa-sha2-nistp256`, `ssh-rsa`, `x509`...). */
    keyType: z.string().min(1).max(64),
    firstSeenAt: isoDateTimeSchema,
    firstSeenSessionId: idSchema,
    trustedBy: idSchema,
    /** Set when the key was replaced after a mismatch, by the person at the session who confirmed it (docs §8.2). */
    retrustedAt: isoDateTimeSchema.nullable(),
    retrustedBy: idSchema.nullable(),
    instanceId: instanceIdSchema.nullable().optional(),
})
    .extend(baseEntityFields)
    .strict()
    .superRefine((val, ctx) => {
    if ((val.retrustedAt === null) !== (val.retrustedBy === null)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['retrustedBy'], message: 'retrustedAt and retrustedBy are set together' });
    }
});
/* -------------------------- addresses that are never a target -------------------------- */
/**
 * Destinations the collector (and the broker, and the relay) refuse whatever else is configured: loopback, unspecified,
 * link-local (which includes the cloud metadata address 169.254.169.254), multicast and broadcast, their IPv6 equivalents
 * (and IPv4 addresses written inside IPv6), and the metadata host names. The same table drives the Go implementations
 * (shared/fixtures/remote-access-forbidden-addresses.v1.json is the contract).
 *
 * Returns the reason, or null when the address is not one of these. A host that is not an IP literal is judged by name only
 * here; the collector also judges every ADDRESS it resolves a name to, at connect time.
 */
export function remoteForbiddenAddress(host) {
    // Only ASCII whitespace is trimmed (JavaScript's trim() also strips U+FEFF, NBSP and the like, which Go's does not, and the
    // Go relay and collector implement this same function). Anything outside printable ASCII is refused outright: a host is an
    // IP literal or an ASCII (punycode) name, and look-alike or invisible characters must never reach a resolver.
    const t = host.replace(/^[ \t\n\r\v\f]+|[ \t\n\r\v\f]+$/g, '');
    if (/[^\x21-\x7e]/.test(t))
        return 'invalid-address';
    const h = t.toLowerCase().replace(/^\[|\]$/g, '').replace(/\.$/, '');
    if (h === '')
        return 'empty';
    if (h === 'localhost' || h.endsWith('.localhost'))
        return 'loopback';
    if (h === 'metadata' || (h.startsWith('metadata.') && h.endsWith('.internal')))
        return 'metadata';
    const v4 = parseIPv4(h);
    if (v4)
        return forbiddenV4(v4);
    // Decimal (2130706433), hex (0x7f000001), octal (0177.0.0.1) and short (127.1) spellings of an IPv4 address are refused
    // outright: resolvers differ on what they mean, and the canonical dotted form is the only one we accept.
    if (/^(0x[0-9a-f]*|\d+)(\.(0x[0-9a-f]*|\d+))*$/.test(h))
        return 'non-canonical-ip';
    const v6 = parseIPv6(h);
    if (v6)
        return forbiddenV6(v6);
    // A colon never appears in a host name: whatever else this is, it is a malformed IPv6 literal (including one with a
    // non-canonical IPv4 tail such as ::ffff:127.1), and is refused rather than treated as a name.
    if (h.includes(':'))
        return /\d+\.\d+/.test(h) ? 'non-canonical-ip' : 'invalid-address';
    return null;
}
function parseIPv4(s) {
    const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(s);
    if (!m)
        return null;
    const parts = m.slice(1).map(Number);
    // Reject leading zeros ("010.0.0.1" is ambiguous: octal in some resolvers) and out-of-range octets.
    if (parts.some((p) => p > 255) || m.slice(1).some((p) => p.length > 1 && p.startsWith('0')))
        return null;
    return parts;
}
function forbiddenV4(o) {
    const [a, b, c, d] = o;
    if (a === 127)
        return 'loopback';
    if (a === 0)
        return 'unspecified';
    if (a === 169 && b === 254)
        return c === 169 && d === 254 ? 'metadata' : 'link-local';
    if (a >= 224 && a <= 239)
        return 'multicast';
    if (a === 255 && b === 255 && c === 255 && d === 255)
        return 'broadcast';
    if (a >= 240)
        return 'reserved';
    return null;
}
/** Eight 16-bit groups, or null if it is not a valid IPv6 literal. */
function parseIPv6(s) {
    if (!s.includes(':'))
        return null;
    let str = s.split('%')[0] ?? s; // zone id
    // An embedded IPv4 tail (::ffff:1.2.3.4) becomes two groups.
    const tail = /(\d{1,3}(?:\.\d{1,3}){3})$/.exec(str);
    if (tail) {
        const v4 = parseIPv4(tail[1]);
        if (!v4)
            return null;
        str = str.slice(0, -tail[1].length) + ((v4[0] << 8) | v4[1]).toString(16) + ':' + ((v4[2] << 8) | v4[3]).toString(16);
    }
    if ((str.match(/::/g) ?? []).length > 1)
        return null;
    const [head, rest] = str.split('::');
    const h = head ? head.split(':') : [];
    const r = rest !== undefined && rest !== '' ? rest.split(':') : [];
    // `::` stands for at least one group of zeros.
    if (str.includes('::') && h.length + r.length > 7)
        return null;
    const groups = str.includes('::') ? [...h, ...Array(8 - h.length - r.length).fill('0'), ...r] : h;
    if (groups.length !== 8 || !groups.every((g) => /^[0-9a-f]{1,4}$/.test(g)))
        return null;
    return groups.map((g) => parseInt(g, 16));
}
function forbiddenV6(g) {
    const [g0, g1, g2, g3, g4, g5, g6, g7] = g;
    if (g.slice(0, 7).every((x) => x === 0) && g7 <= 1)
        return g7 === 1 ? 'loopback' : 'unspecified';
    // ::ffff:a.b.c.d (IPv4-mapped) and ::a.b.c.d (IPv4-compatible): judge the embedded IPv4 address.
    const mapped = g0 === 0 && g1 === 0 && g2 === 0 && g3 === 0 && g4 === 0 && g5 === 0xffff;
    const compat = g0 === 0 && g1 === 0 && g2 === 0 && g3 === 0 && g4 === 0 && g5 === 0;
    if (mapped || compat)
        return forbiddenV4([g6 >> 8, g6 & 255, g7 >> 8, g7 & 255]);
    // SIIT ::ffff:0:a.b.c.d (RFC 2765) embeds an IPv4 address: judge it.
    if (g0 === 0 && g1 === 0 && g2 === 0 && g3 === 0 && g4 === 0xffff && g5 === 0) {
        return forbiddenV4([g6 >> 8, g6 & 255, g7 >> 8, g7 & 255]);
    }
    // Teredo 2001::/32 tunnels IPv4 and obfuscates the address it embeds: refuse the whole range.
    if (g0 === 0x2001 && g1 === 0)
        return 'reserved';
    if ((g0 & 0xffc0) === 0xfe80)
        return 'link-local';
    // fec0::/10, deprecated site-local space: never a target.
    if ((g0 & 0xffc0) === 0xfec0)
        return 'reserved';
    // NAT64 well-known prefix 64:ff9b::/96 carries an IPv4 address in its last 32 bits: judge that address.
    if (g0 === 0x64 && g1 === 0xff9b && g2 === 0 && g3 === 0 && g4 === 0 && g5 === 0) {
        return forbiddenV4([g6 >> 8, g6 & 255, g7 >> 8, g7 & 255]);
    }
    // 64:ff9b:1::/48 (local-use NAT64, RFC 8215) embeds the address at a prefix-length-dependent offset: refuse the whole range.
    if (g0 === 0x64 && g1 === 0xff9b && g2 === 1)
        return 'reserved';
    // 6to4 2002::/16 carries an IPv4 address in the next 32 bits: judge that address.
    if (g0 === 0x2002)
        return forbiddenV4([g1 >> 8, g1 & 255, g2 >> 8, g2 & 255]);
    if ((g0 & 0xff00) === 0xff00)
        return 'multicast';
    // fd00:ec2::254 is the AWS IPv6 metadata address.
    if (g0 === 0xfd00 && g1 === 0x0ec2 && g2 === 0 && g3 === 0 && g4 === 0 && g5 === 0 && g6 === 0 && g7 === 0x0254)
        return 'metadata';
    return null;
}
//# sourceMappingURL=remote-access.schema.js.map